import { Component, ReactNode, ErrorInfo } from 'react';
import { logger } from '../utils/logger';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Error Boundary to catch React errors and prevent full app crash
 * Preserves draft state and allows recovery
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log error
    logger.error('React Error Boundary caught error', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack
    });

    // Save error to localStorage for debugging
    try {
      const errorReport = {
        timestamp: new Date().toISOString(),
        error: {
          message: error.message,
          stack: error.stack
        },
        componentStack: errorInfo.componentStack,
        url: window.location.href,
        userAgent: navigator.userAgent
      };
      
      localStorage.setItem('last_error_report', JSON.stringify(errorReport));
    } catch (e) {
      console.error('Failed to save error report:', e);
    }

    this.setState({
      error,
      errorInfo
    });

    // Call custom error handler if provided
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleReload = () => {
    // Clear error state and reload
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
    
    // Reload the page
    window.location.reload();
  };

  handleReset = () => {
    // Just reset error state without reloading
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // Default error UI
      return (
        <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
          <div className="max-w-2xl w-full bg-white rounded-xl shadow-lg p-8">
            <div className="flex items-center mb-6">
              <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mr-4">
                <i className="ri-error-warning-line text-red-600 text-2xl"></i>
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Bir Hata Oluştu</h1>
                <p className="text-gray-600 text-sm">Uygulama beklenmeyen bir hatayla karşılaştı</p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm font-semibold text-red-900 mb-2">Hata Detayı:</p>
              <p className="text-sm text-red-700 font-mono break-all">
                {this.state.error?.message}
              </p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex items-start">
                <i className="ri-information-line text-blue-600 text-lg mr-3 mt-0.5"></i>
                <div className="text-sm text-blue-900">
                  <p className="font-semibold mb-1">Çalışmalarınız Güvende</p>
                  <p className="text-blue-700">
                    Kaydedilmemiş değişiklikleriniz tarayıcıda saklandı. 
                    Sayfayı yenilediğinizde kaldığınız yerden devam edebilirsiniz.
                  </p>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={this.handleReload}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-600 to-indigo-600 text-white rounded-lg font-semibold hover:from-blue-700 hover:to-indigo-700 transition-all duration-200 flex items-center justify-center whitespace-nowrap"
              >
                <i className="ri-refresh-line mr-2"></i>
                Sayfayı Yenile
              </button>
              
              <button
                onClick={this.handleReset}
                className="px-6 py-3 bg-white border-2 border-gray-300 text-gray-700 rounded-lg font-semibold hover:bg-gray-50 transition-all duration-200 flex items-center justify-center whitespace-nowrap"
              >
                <i className="ri-arrow-go-back-line mr-2"></i>
                Tekrar Dene
              </button>
            </div>

            {/* Geliştirici Detayları - NODE_ENV kontrolü kaldırıldı */}
            {this.state.error && (
              <details className="mt-6">
                <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-900 font-semibold">
                  Geliştirici Detayları
                </summary>
                <pre className="mt-3 text-xs bg-gray-900 text-gray-100 p-4 rounded-lg overflow-auto max-h-64">
                  {this.state.error.stack}
                  {'\n\n'}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
